import React, { useState, useEffect } from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col, Button } from 'reactstrap';
import { fetchFormData } from '../../../../Api/ApiMethods';
import axios from 'axios';
import FilesComponent from '../Component/FilesComponent';

const PowerOfAttorneyForm = ({ formData, errors, setFormData, handleInputChange, customerId, powerOfAttorneyId, fieldPermissions, canEdit = true }) => {
    const [availablePlates, setAvailablePlates] = useState([]);
    const [selectedPlateId, setSelectedPlateId] = useState('');
    const [selectedPlates, setSelectedPlates] = useState([]);
    const [loading, setLoading] = useState(false);    // Helper function to check field access based on permissions
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled
        if (!canEdit) {
            return {
                disabled: true,
                required: false,
            };
        }

        // Ensure fieldPermissions is an array before calling .find()
        const safeFieldPermissions = Array.isArray(fieldPermissions) ? fieldPermissions : [];
        const permission = safeFieldPermissions.find(p => p.fieldName.toLowerCase() === fieldName.toLowerCase());
        return {
            disabled: permission ? !permission.editable : false,
            required: permission ? permission.required : false,
        };
    };

    // Define field access (verify API field names!)
    const poaNumberAccess = getFieldAccess('powerofattorney.poanumber');
    const notaryNumberAccess = getFieldAccess('powerofattorney.notaryofpoa'); // API uses notaryOfPoa
    const poaDateAccess = getFieldAccess('powerofattorney.poadate');
    const isPoaTimedAccess = getFieldAccess('powerofattorney.ispoatimelimited');
    const poaEndDateAccess = getFieldAccess('powerofattorney.poaenddate');
    const isPlatesLimitedAccess = getFieldAccess('powerofattorney.islicenseplateslimited');
    const plateLimitAccess = getFieldAccess('powerofattorney.licenseplatelimit'); // Permission for the list itself?
    const isAccidentDateLimitedAccess = getFieldAccess('powerofattorney.isaccidentdatelimited');
    const plateLimitAccidentDateAccess = getFieldAccess('powerofattorney.licenseplatelimitaccidentdate');
    const originalArrivedAccess = getFieldAccess('powerofattorney.poaoriginalarrived');
    const contractArrivedAccess = getFieldAccess('customer.contractoriginalarrived'); // Check if this is the correct API field

    // Fetch available plates for the customer when license plates limited option is selected
    useEffect(() => {
        const loadCustomerPlates = async () => {
            if (formData.isLicensePlatesLimited === "yes" && !isPlatesLimitedAccess.disabled) { // Only fetch if enabled
                try {
                    // Determine which customer ID to use (from props or from form data)
                    const customerIdToUse = customerId || formData.customerId;

                    console.log('Fetching plates for customer ID:', customerIdToUse);

                    if (!customerIdToUse) {
                        console.warn('No customer ID available to fetch plates');
                        return;
                    }

                    setLoading(true);

                    // Use the environment variable for the API URL
                    const apiUrl = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/licenseplate'}`;
                    console.log('Making API call to:', apiUrl);

                    const response = await axios.get(apiUrl, {
                        params: {
                            page: 1,
                            pageSize: 50,
                            customer: customerIdToUse
                        }
                    });

                    console.log('License plate API response:', response.data);

                    // Process the response data
                    if (response.data && response.data.isSuccess && response.data.data && response.data.data.items) {
                        setAvailablePlates(response.data.data.items);
                    } else if (response.data && response.data.items) {
                        setAvailablePlates(response.data.items);
                    } else if (Array.isArray(response.data)) {
                        setAvailablePlates(response.data);
                    } else {
                        console.warn('No valid plate data in response:', response.data);
                        setAvailablePlates([]);
                    }
                } catch (error) {
                    console.error('Error loading plates:', error);
                    setAvailablePlates([]);
                } finally {
                    setLoading(false);
                }
            } else {
                // Clear plate list if license plates are not limited or field is disabled
                setAvailablePlates([]);
            }
        };

        loadCustomerPlates();
    }, [formData.isLicensePlatesLimited, customerId, formData.recipientOfPowerOfAttorney, isPlatesLimitedAccess.disabled]); // Add disabled state dependency

    // Load selected plates when component mounts or plateLimit changes
    useEffect(() => {
        const loadSelectedPlates = async () => {
            if (formData.plateLimit && Array.isArray(formData.plateLimit) && formData.plateLimit.length > 0) {
                console.log('Loading selected plates from formData.plateLimit:', formData.plateLimit);

                setLoading(true);
                const platesData = [];

                for (const plateId of formData.plateLimit) {
                    try {
                        // Use the environment variable for the API URL
                        const apiUrl = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/licenseplate/${plateId}`;

                        const response = await axios.get(apiUrl);
                        console.log(`Fetched plate data for ID ${plateId}:`, response.data);

                        if (response.data && response.data.isSuccess && response.data.data) {
                            platesData.push(response.data.data);
                        } else if (response.data) {
                            platesData.push(response.data);
                        }
                    } catch (error) {
                        console.error(`Error fetching plate ${plateId}:`, error);
                    }
                }

                console.log('Setting selected plates:', platesData);
                setSelectedPlates(platesData);
                setLoading(false);
            } else {
                console.log('No plateLimit data to load');
                setSelectedPlates([]);
            }
        };

        loadSelectedPlates();
    }, [formData.plateLimit]);

    // Add plate to selection
    const handleAddPlate = () => {
        if (!selectedPlateId) {
            console.warn('No plate selected to add');
            return;
        }

        console.log('Adding plate ID:', selectedPlateId);

        const plateToAdd = availablePlates.find(p => p.id === selectedPlateId);
        if (plateToAdd && !selectedPlates.find(p => p.id === plateToAdd.id)) {
            console.log('Found plate to add:', plateToAdd);

            setSelectedPlates(prev => [...prev, plateToAdd]);
            setFormData(prev => ({
                ...prev,
                plateLimit: Array.isArray(prev.plateLimit)
                    ? [...prev.plateLimit, selectedPlateId]
                    : [selectedPlateId]
            }));
        }
        setSelectedPlateId(''); // Reset selection
    };

    // Remove plate from selection
    const handlePlateRemove = (plateId) => {
        console.log('Removing plate ID:', plateId);

        setSelectedPlates(prev => prev.filter(p => p.id !== plateId));
        setFormData(prev => ({
            ...prev,
            plateLimit: Array.isArray(prev.plateLimit)
                ? prev.plateLimit.filter(id => id !== plateId)
                : []
        }));
    };

    return (
        <>
            <H5>Vekalet Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <Row form>
                <Col md={6}>
                    <FormGroup>
                        <Label for="powerOfAttorneyNumber">Vekalet No {poaNumberAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            id="powerOfAttorneyNumber"
                            name="powerOfAttorneyNumber"
                            value={formData.powerOfAttorneyNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Otomatik atanacak"
                            disabled={true}
                            required={poaNumberAccess.required}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="notaryNumber">Vekalet Veren Noter {notaryNumberAccess.required && <span className="text-danger">*</span>}</Label>
                        <Input
                            id="notaryNumber"
                            name="notaryNumber"
                            value={formData.notaryNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Örn: Ankara 15. Noter"
                            invalid={!!errors.notaryNumber}
                            disabled={notaryNumberAccess.disabled}
                            required={notaryNumberAccess.required}
                        />
                        <FormFeedback>{errors.notaryNumber}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label for="powerOfAttorneyDate">Vekalet Tarihi {poaDateAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    type="date"
                    id="powerOfAttorneyDate"
                    name="powerOfAttorneyDate"
                    value={formData.powerOfAttorneyDate instanceof Date && !isNaN(formData.powerOfAttorneyDate)
                        ? formData.powerOfAttorneyDate.toISOString().slice(0, 10)
                        : ''}
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'powerOfAttorneyDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.powerOfAttorneyDate}
                    disabled={poaDateAccess.disabled}
                    required={poaDateAccess.required}
                />
                <FormFeedback>{errors.powerOfAttorneyDate}</FormFeedback>
            </FormGroup>

            <FormGroup className="d-flex align-items-center mb-3">
                <Label for="isPowerOfAttorneyTimed" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                    Vekalet Süreli Mi? {isPoaTimedAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="isPowerOfAttorneyTimedTrue"
                            type="radio"
                            name="isPowerOfAttorneyTimed"
                            value="yes"
                            checked={formData.isPowerOfAttorneyTimed === "yes"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isPowerOfAttorneyTimed: "yes",
                                }))
                            }
                            invalid={!!errors.isPowerOfAttorneyTimed}
                            disabled={isPoaTimedAccess.disabled}
                            required={isPoaTimedAccess.required}
                        />
                        <Label for="isPowerOfAttorneyTimedTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="isPowerOfAttorneyTimedFalse"
                            type="radio"
                            name="isPowerOfAttorneyTimed"
                            value="no"
                            checked={formData.isPowerOfAttorneyTimed === "no"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isPowerOfAttorneyTimed: "no",
                                    powerOfAttorneyEndDate: null,
                                }))
                            }
                            invalid={!!errors.isPowerOfAttorneyTimed}
                            disabled={isPoaTimedAccess.disabled}
                        />
                        <Label for="isPowerOfAttorneyTimedFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                    <FormFeedback>{errors.isPowerOfAttorneyTimed}</FormFeedback>
                </div>
            </FormGroup>

            {formData.isPowerOfAttorneyTimed === "yes" && (
                <FormGroup>
                    <Label for="powerOfAttorneyEndDate">Vekalet Bitiş Tarihi {poaEndDateAccess.required && <span className="text-danger">*</span>}</Label>
                    <Input
                        type="date"
                        id="powerOfAttorneyEndDate"
                        name="powerOfAttorneyEndDate"
                        value={formData.powerOfAttorneyEndDate instanceof Date && !isNaN(formData.powerOfAttorneyEndDate)
                            ? formData.powerOfAttorneyEndDate.toISOString().slice(0, 10)
                            : ''}
                        onChange={(e) => {
                            const value = e.target.value ? new Date(e.target.value) : null;
                            handleInputChange({
                                target: {
                                    name: 'powerOfAttorneyEndDate',
                                    value
                                }
                            });
                        }}
                        invalid={!!errors.powerOfAttorneyEndDate}
                        disabled={poaEndDateAccess.disabled}
                        required={poaEndDateAccess.required}
                    />
                    <FormFeedback>{errors.powerOfAttorneyEndDate}</FormFeedback>
                </FormGroup>
            )}

            <FormGroup className="d-flex align-items-center mb-3">
                <Label for="isLicensePlatesLimited" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                    Plaka Sınırlaması Var Mı? {isPlatesLimitedAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="isLicensePlatesLimitedTrue"
                            type="radio"
                            name="isLicensePlatesLimited"
                            value="yes"
                            checked={formData.isLicensePlatesLimited === "yes"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isLicensePlatesLimited: "yes",
                                }))
                            }
                            invalid={!!errors.isLicensePlatesLimited}
                            disabled={isPlatesLimitedAccess.disabled}
                            required={isPlatesLimitedAccess.required}
                        />
                        <Label for="isLicensePlatesLimitedTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="isLicensePlatesLimitedFalse"
                            type="radio"
                            name="isLicensePlatesLimited"
                            value="no"
                            checked={formData.isLicensePlatesLimited === "no" || formData.isLicensePlatesLimited === null}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isLicensePlatesLimited: "no",
                                    plateLimit: [],
                                    isAccidentDateLimited: "no",
                                    plateLimitAccidentDate: null
                                }))
                            }
                            invalid={!!errors.isLicensePlatesLimited}
                            disabled={isPlatesLimitedAccess.disabled}
                        />
                        <Label for="isLicensePlatesLimitedFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                    <FormFeedback>{errors.isLicensePlatesLimited}</FormFeedback>
                </div>
            </FormGroup>

            {formData.isLicensePlatesLimited === "yes" && (
                <>
                    <FormGroup>
                        <Label for="licensePlateSelect">Plaka Seçimi {plateLimitAccess.required && <span className="text-danger">*</span>}</Label>
                        <div className="d-flex">
                            <Input
                                type="select"
                                id="licensePlateSelect"
                                value={selectedPlateId}
                                onChange={(e) => setSelectedPlateId(e.target.value)}
                                className="me-2  me-2"
                                disabled={loading || plateLimitAccess.disabled}
                            >
                                <option value="">Plaka Seçiniz</option>
                                {availablePlates
                                    .filter(plate => !selectedPlates.find(p => p.id === plate.id))
                                    .map(plate => (
                                        <option key={plate.id} value={plate.id}>
                                            {plate.licensePlateNumber || plate.plateNumber}
                                        </option>
                                    ))
                                }
                            </Input>
                            <Button
                                color="primary"
                                onClick={handleAddPlate}
                                disabled={!selectedPlateId || loading || plateLimitAccess.disabled}
                            >
                                {loading ? 'Yükleniyor...' : 'Ekle'}
                            </Button>
                        </div>
                        {plateLimitAccess.required && selectedPlates.length === 0 && <div className="text-danger small mt-1">En az bir plaka seçimi zorunludur.</div>}
                    </FormGroup>

                    {loading && (
                        <div className="text-center my-3">
                            <span>Plakalar yükleniyor...</span>
                        </div>
                    )}

                    <div className="selected-plates">
                        {selectedPlates.map(plate => (
                            <div
                                key={plate.id}
                                className="selected-plate"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px',
                                    margin: '4px 0',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px'
                                }}
                            >
                                <span>{plate.licensePlateNumber || plate.plateNumber}</span>
                                <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handlePlateRemove(plate.id)}
                                    disabled={loading || plateLimitAccess.disabled}
                                >
                                    Kaldır
                                </Button>
                            </div>
                        ))}
                    </div>

                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label for="isAccidentDateLimited" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                            Kaza Tarihi Sınırlaması Var Mı? {isAccidentDateLimitedAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="isAccidentDateLimitedTrue"
                                    type="radio"
                                    name="isAccidentDateLimited"
                                    value="yes"
                                    checked={formData.isAccidentDateLimited === "yes"}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            isAccidentDateLimited: "yes",
                                            // Automatically set the accident date limit to match the accident date from AccidentInfoForm
                                            plateLimitAccidentDate: prevFormData.accidentDate || null,
                                        }))
                                    }
                                    invalid={!!errors.isAccidentDateLimited}
                                    disabled={isAccidentDateLimitedAccess.disabled}
                                    required={isAccidentDateLimitedAccess.required}
                                />
                                <Label for="isAccidentDateLimitedTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="isAccidentDateLimitedFalse"
                                    type="radio"
                                    name="isAccidentDateLimited"
                                    value="no"
                                    checked={formData.isAccidentDateLimited === "no" || formData.isAccidentDateLimited === null}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            isAccidentDateLimited: "no",
                                            plateLimitAccidentDate: null
                                        }))
                                    }
                                    invalid={!!errors.isAccidentDateLimited}
                                    disabled={isAccidentDateLimitedAccess.disabled}
                                />
                                <Label for="isAccidentDateLimitedFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                            <FormFeedback>{errors.isAccidentDateLimited}</FormFeedback>
                        </div>
                    </FormGroup>

                    {formData.isAccidentDateLimited === "yes" && (
                        <FormGroup>
                            <Label for="plateLimitAccidentDate">Kaza Tarihi Sınırı {plateLimitAccidentDateAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="date"
                                id="plateLimitAccidentDate"
                                name="plateLimitAccidentDate"
                                value={formData.plateLimitAccidentDate instanceof Date && !isNaN(formData.plateLimitAccidentDate)
                                    ? formData.plateLimitAccidentDate.toISOString().slice(0, 10)
                                    : formData.accidentDate instanceof Date && !isNaN(formData.accidentDate)
                                    ? formData.accidentDate.toISOString().slice(0, 10)
                                    : ''}
                                onChange={(e) => {
                                    const value = e.target.value ? new Date(e.target.value) : null;
                                    handleInputChange({
                                        target: {
                                            name: 'plateLimitAccidentDate',
                                            value
                                        }
                                    });
                                }}
                                invalid={!!errors.plateLimitAccidentDate}
                                disabled={plateLimitAccidentDateAccess.disabled}
                                required={plateLimitAccidentDateAccess.required}
                            />
                            <FormFeedback>{errors.plateLimitAccidentDate}</FormFeedback>
                        </FormGroup>
                    )}
                </>
            )}

            <FormGroup className="d-flex align-items-center mb-3">
                <Label for="hasOriginalArrivedRadio" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                    Vekalet Aslı Geldi Mi? {originalArrivedAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="hasOriginalArrivedTrue"
                            type="radio"
                            name="hasOriginalArrived"
                            value="yes"
                            checked={formData.hasOriginalArrived === "yes"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    hasOriginalArrived: "yes",
                                }))
                            }
                            invalid={!!errors.hasOriginalArrived}
                            disabled={originalArrivedAccess.disabled}
                            required={originalArrivedAccess.required}
                        />
                        <Label for="hasOriginalArrivedTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="hasOriginalArrivedFalse"
                            type="radio"
                            name="hasOriginalArrived"
                            value="no"
                            checked={formData.hasOriginalArrived === "no" || formData.hasOriginalArrived === null}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    hasOriginalArrived: "no",
                                }))
                            }
                            invalid={!!errors.hasOriginalArrived}
                            disabled={originalArrivedAccess.disabled}
                        />
                        <Label for="hasOriginalArrivedFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                    <FormFeedback>{errors.hasOriginalArrived}</FormFeedback>
                </div>
            </FormGroup>

            <FormGroup className="d-flex align-items-center mb-3">
                <Label for="hasContractArrivedRadio" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                    Sözleşme Aslı Geldi Mi? {contractArrivedAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="hasContractArrivedTrue"
                            type="radio"
                            name="hasContractArrived"
                            value="yes"
                            checked={formData.hasContractArrived === "yes"}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    hasContractArrived: "yes",
                                }))
                            }
                            invalid={!!errors.hasContractArrived}
                            disabled={contractArrivedAccess.disabled}
                            required={contractArrivedAccess.required}
                        />
                        <Label for="hasContractArrivedTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="hasContractArrivedFalse"
                            type="radio"
                            name="hasContractArrived"
                            value="no"
                            checked={formData.hasContractArrived === "no" || formData.hasContractArrived === null}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    hasContractArrived: "no",
                                }))
                            }
                            invalid={!!errors.hasContractArrived}
                            disabled={contractArrivedAccess.disabled}
                        />
                        <Label for="hasContractArrivedFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                    <FormFeedback>{errors.hasContractArrived}</FormFeedback>
                </div>
            </FormGroup>

            {/* Vekalet Belgesi Dosya Yükleme */}
            <div className="mt-4">
                {!powerOfAttorneyId && (
                    <div className="alert alert-info mb-3">
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>Bilgi:</strong> Vekalet belgesi yükleyebilmek için önce çağrı formunu kaydetmeniz gerekiyor.
                    </div>
                )}
                
                <FilesComponent
                    onFilesChange={(files) => {
                        // Files değişikliklerini handle edebiliriz (opsiyonel)
                        console.log('PowerOfAttorney files changed:', files);
                    }}
                    multiple={true}
                    callFormReference={false} // Bu powerOfAttorney için
                    referenceId={powerOfAttorneyId} // PowerOfAttorney ID'si
                    typeFilter={['VEKALETNAME']} // Sadece VEKALETNAME türündeki dosyalar
                    title="Vekalet Belgeleri"
                    canEdit={canEdit}
                />
            </div>

            {/* Sözleşme Belgesi Dosya Yükleme */}
                <div className="mt-4">
                    {!customerId && (
                        <div className="alert alert-info mb-3">
                            <i className="fa fa-info-circle me-2"></i>
                            <strong>Bilgi:</strong> Sözleşme belgesi yükleyebilmek için önce çağrı formunu kaydetmeniz gerekiyor.
                        </div>
                    )}
                    
                    <FilesComponent
                        onFilesChange={(files) => {
                            // Files değişikliklerini handle edebiliriz (opsiyonel)
                            console.log('Customer contract files changed:', files);
                        }}
                        multiple={true}
                        callFormReference={false} // Bu customer için
                        referenceId={customerId} // Customer ID'si
                        typeFilter={['SÖZLEŞME']} // Sadece SÖZLEŞME türündeki dosyalar
                        title="Sözleşme Belgeleri"
                        canEdit={canEdit}
                    />
                </div>
        </>
    );
};

export default PowerOfAttorneyForm;
